<template>
  <transition name="fade" v-if="open">
    <div
      class="backdrop"
      @click="checkOutsideClick"
      @keydown.esc.prevent="handleEscape"
    >
      <component :is="bare ? 'div' : 'ui-focus-lock'">
        <dialog
          :open="open"
          class="dialog"
          :class="{ 'dialog--bare': bare }"
          :style="dialogStyle"
          ref="dialog"
        >
          <slot />
        </dialog>
      </component>
    </div>
  </transition>
</template>

<script>
export default {
  props: {
    open: Boolean,
    parent: { default: () => document.body },
    width: { type: String, default: null },
    // Skips UiFocusLock (it doubles up with the island's own shadow-DOM focus
    // trap) and this component's Escape/outside-click handling, leaving those
    // to the bare consumer. Defaults false so existing call sites are unchanged.
    bare: { type: Boolean, default: false }
  },
  computed: {
    dialogStyle() {
      return this.width ? { width: this.width } : null;
    }
  },
  methods: {
    checkOutsideClick(event) {
      if (this.bare) return;
      if (!this.open) return;
      if (this.$refs.dialog.contains(event.target)) return;
      this.close();
    },
    handleEscape() {
      if (this.bare) return;
      this.close();
    },
    close() {
      this.$emit("update:open", false);
    }
  },
  mounted() {
    this.parent.appendChild(this.$el);
  },
  destroyed() {
    this.$el?.remove();
  }
};
</script>

<style scoped lang="scss">
.backdrop {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  box-sizing: border-box;
  overflow: auto;
  background: var(--overlay);
}
.dialog {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  box-sizing: border-box;
  display: block;
  width: 50rem;
  max-width: 100%;
  padding: 1rem;
  margin: 1vh auto;
  color: var(--text-color-base);
  background: var(--card-background);
  border: 0.25rem solid var(--button-default-border-color);
  border-radius: 0.5rem;
  box-shadow: 0 0.25rem 1rem var(--shadow-color);
}
// A bare dialog docks to the right edge full-height (AML modal is a
// drawer, not a centered card) and hands its inner padding to the consumer,
// which owns its own scroll regions.
.dialog--bare {
  top: 0;
  right: 0;
  bottom: 0;
  left: auto;
  height: 100%;
  padding: 0;
  margin: 0;
  border-width: 0 0 0 0.25rem;
  border-radius: 0;
}
.fade {
  &-enter-active,
  &-leave-active {
    pointer-events: none;
    transition-duration: var(--animation-fast);
    transition-property: opacity;
  }
  &-enter-to,
  &-leave {
    opacity: 1;
  }
  &-enter,
  &-leave-to {
    opacity: 0;
  }
  // A bare dialog is a drawer, not a fading card with a slide animation. Done with a
  // descendant selector off the enter/leave classes Vue toggles on .backdrop,
  // since <transition> only tracks its direct child.
  &-enter-active .dialog--bare,
  &-leave-active .dialog--bare {
    transition-duration: var(--animation-fast);
    transition-property: transform;
  }
  &-enter-to .dialog--bare,
  &-leave .dialog--bare {
    transform: translateX(0);
  }
  &-enter .dialog--bare,
  &-leave-to .dialog--bare {
    transform: translateX(100%);
  }
}
</style>
